import { SxProps, Theme } from "@mui/material/styles";

import { themeColors, getColor } from "../theme/colorTokens";

/**
 * Common MUI + Tailwind style utilities for consistent component styling
 */

// Button variants using theme colors
export const buttonVariants = {
  primary: {
    className:
      "font-semibold text-white transition-all duration-200 hover:shadow-lg",
    sx: {
      backgroundColor: themeColors.primary.main,
      "&:hover": { backgroundColor: themeColors.primary.dark },
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 600,
    } as SxProps<Theme>,
  },
  secondary: {
    className: "font-semibold transition-all duration-200 hover:bg-opacity-10",
    sx: {
      borderColor: themeColors.primary.main,
      color: themeColors.primary.main,
      "&:hover": {
        borderColor: themeColors.primary.dark,
        backgroundColor: themeColors.primary.light,
      },
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 600,
    } as SxProps<Theme>,
  },
  google: {
    className: "font-semibold transition-all duration-200 hover:shadow-md",
    sx: {
      borderColor: themeColors.border.main,
      color: themeColors.text.primary,
      "&:hover": {
        borderColor: themeColors.border.dark,
        backgroundColor: themeColors.background.default,
      },
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 600,
    } as SxProps<Theme>,
  },
};

// Layout classes for common patterns (now using theme-aware classes)
export const layoutStyles = {
  fullscreen:
    "min-h-screen bg-surface-background flex items-center justify-center p-4",
  centered: "flex items-center justify-center",
  card: "w-full max-w-md mx-auto",
  form: "flex flex-col space-y-6",
  buttonGroup: "flex flex-col space-y-3 mt-6",
  divider: "flex items-center my-6",
};

// Input styles using theme
export const getInputStyles = () => ({
  className: "transition-all duration-200",
  sx: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      backgroundColor: themeColors.background.paper,
      "& fieldset": {
        borderColor: themeColors.border.light,
      },
      "&:hover fieldset": {
        borderColor: themeColors.border.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: themeColors.primary.main,
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: themeColors.error[700],
        borderWidth: "3px",
      },
      "&.Mui-error:hover fieldset": {
        borderColor: themeColors.error[700],
        borderWidth: "3px",
      },
      "&.Mui-error.Mui-focused fieldset": {
        borderColor: themeColors.error[700],
        borderWidth: "3px",
      },
    },
    "& .MuiInputLabel-root": {
      color: themeColors.text.secondary,
      "&.Mui-focused": {
        color: themeColors.primary.main,
      },
      "&.Mui-error": {
        color: themeColors.error[700],
      },
      "&.Mui-error.Mui-focused": {
        color: themeColors.error[700],
      },
    },
    "& .MuiFormHelperText-root": {
      "&.Mui-error": {
        color: themeColors.error[700],
        fontWeight: 500,
      },
    },
  } as SxProps<Theme>,
});

// Typography classes using theme colors
export const typographyClasses = {
  heading: "text-gray-dark font-bold text-center mb-2",
  subtitle: "text-gray-text text-center mb-6",
  link: "text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer font-medium",
};

// Alert styles using theme
export const getAlertStyles = (
  severity: "info" | "success" | "warning" | "error",
) => ({
  className: "mb-4 rounded-xl",
  sx: {
    backgroundColor: getColor(`${severity}.50`),
    color: getColor(`${severity}.700`),
    borderRadius: "12px",
    "& .MuiAlert-icon": {
      color: getColor(`${severity}.600`),
    },
  } as SxProps<Theme>,
});
